export const ZONE_PROXIMITY_TEST_PIPS = 15;
export const ZONE_FAILURE_BUFFER_PIPS = 15;

export const DEFAULT_ZONE_OPTIONS = {
  consolidationBars: 3,
  maxConsolidationSpreadMultiplier: 2.4,
  impulseCheckBars: 4,
  impulseMultiplier: 0.35,
  minZoneSizeMultiplier: 0.05,
  maxZoneSizeMultiplier: 6,
  maxZonesPerType: 18,
  spikeLookback: 14,
  proximityTestPips: ZONE_PROXIMITY_TEST_PIPS,
  failureBufferPips: ZONE_FAILURE_BUFFER_PIPS,
};

export function averageRange(candles, lookback = 100) {
  if (!candles.length) return 0;

  const slice = candles.slice(-lookback);
  const total = slice.reduce(
    (sum, candle) => sum + Math.abs(candle.high - candle.low),
    0
  );

  return total / slice.length;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function getZoneDisplayLabel(zone, { failed = false, tested = false } = {}) {
  if (zone.zoneName !== "demand" && zone.zoneName !== "supply") {
    return zone.zoneLabel || zone.name || "Zone";
  }

  if (failed) {
    return zone.isDemand ? "Failed Demand" : "Failed Supply";
  }

  if (tested) {
    return zone.isDemand ? "Tested Demand" : "Tested Supply";
  }

  return zone.isDemand ? "Fresh Demand" : "Fresh Supply";
}

function getRecentAverageRange(candles, currentIndex, lookback) {
  const fromIndex = Math.max(0, currentIndex - lookback);
  const sample = candles.slice(fromIndex, currentIndex);

  if (!sample.length) {
    return Math.abs(candles[currentIndex]?.high - candles[currentIndex]?.low) || 0;
  }

  return averageRange(sample, sample.length);
}

function isSpikeCandle(candle, candles, currentIndex, options) {
  if (!candle) {
    return false;
  }

  const bodySize = Math.abs((candle.close ?? 0) - (candle.open ?? 0));
  const upperWick = Math.max(0, (candle.high ?? 0) - Math.max(candle.open ?? 0, candle.close ?? 0));
  const lowerWick = Math.max(0, Math.min(candle.open ?? 0, candle.close ?? 0) - (candle.low ?? 0));
  const wickSize = Math.max(upperWick, lowerWick);
  const candleRange = Math.abs((candle.high ?? 0) - (candle.low ?? 0));
  const recentAverageRange = getRecentAverageRange(
    candles,
    currentIndex,
    options.spikeLookback ?? DEFAULT_ZONE_OPTIONS.spikeLookback
  );

  return wickSize >= Math.max(bodySize, candleRange * 0.1) * 2 || candleRange > recentAverageRange;
}

function isProximityRetest(zone, candle, options) {
  const proximityBuffer =
    Number(options.proximityTestPips) || DEFAULT_ZONE_OPTIONS.proximityTestPips;

  if (zone.isDemand) {
    return candle.close > zone.high && candle.close <= zone.high + proximityBuffer;
  }

  return candle.close < zone.low && candle.close >= zone.low - proximityBuffer;
}

function isFailureClose(zone, candle, options) {
  const failureBuffer =
    Number(options.failureBufferPips) || DEFAULT_ZONE_OPTIONS.failureBufferPips;

  if (zone.isDemand) {
    return candle.close < zone.low - failureBuffer;
  }

  return candle.close > zone.high + failureBuffer;
}

function getRetestDepth(zone, candle) {
  const zoneRange = Math.max(zone.range || zone.high - zone.low || 0, 0.0000001);

  if (zone.isDemand) {
    const touchLow = Math.min(zone.high, Math.max(zone.low, candle.low));
    return clamp((zone.high - touchLow) / zoneRange, 0, 1.5);
  }

  const touchHigh = Math.max(zone.low, Math.min(zone.high, candle.high));
  return clamp((touchHigh - zone.low) / zoneRange, 0, 1.5);
}

function hasDirectionalRejection(zone, candle) {
  if (!candle) {
    return false;
  }

  const body = Math.abs((candle.close ?? 0) - (candle.open ?? 0));
  const candleRange = Math.max(
    Math.abs((candle.high ?? 0) - (candle.low ?? 0)),
    0.0000001
  );
  const closesDirectional = zone.isDemand
    ? candle.close > candle.open && candle.close >= zone.low
    : candle.close < candle.open && candle.close <= zone.high;
  const closesAwayFromZone = zone.isDemand
    ? candle.close >= zone.low + zone.range * 0.2
    : candle.close <= zone.high - zone.range * 0.2;
  const bodyStrength = body / candleRange >= 0.3;

  return closesDirectional && closesAwayFromZone && bodyStrength;
}

function computeZoneQuality(zone, averageCandleRange = 0) {
  const normalizedRangeBase = Math.max(averageCandleRange, 0.0000001);
  const normalizedImpulse = clamp(
    (zone.impulseDistance ?? 0) / normalizedRangeBase,
    0,
    4
  );
  const rangeEfficiency = clamp(
    (zone.range ?? 0) / normalizedRangeBase,
    0.2,
    2.5
  );
  const freshnessWeight =
    zone.state === "fresh" ? 1.15 : zone.state === "tested" ? 0.95 : 0.55;
  const depthPenalty =
    zone.tested && zone.testDepth != null ? Math.max(0.72, 1 - zone.testDepth * 0.2) : 1;

  return Number(
    (normalizedImpulse * 0.55 + rangeEfficiency * 0.45) *
      freshnessWeight *
      depthPenalty
  .toFixed(3));
}

export function getAnalyzedCandles(candles) {
  if (!Array.isArray(candles) || candles.length === 0) {
    return [];
  }

  const lastCandle = candles[candles.length - 1];

  if (lastCandle?.isClosed === false) {
    return candles.slice(0, -1);
  }

  return candles;
}

export function getRangeStats(candles, fromIndex, toIndex) {
  let high = Number.NEGATIVE_INFINITY;
  let low = Number.POSITIVE_INFINITY;

  for (let index = fromIndex; index <= toIndex; index += 1) {
    high = Math.max(high, candles[index].high);
    low = Math.min(low, candles[index].low);
  }

  return {
    high,
    low,
    range: high - low,
  };
}

export function scoreImpulse(
  candles,
  breakoutIndex,
  impulseCheckBars,
  boundary,
  isDemand
) {
  let bestClose = candles[breakoutIndex].close;

  for (
    let index = breakoutIndex;
    index <= Math.min(candles.length - 1, breakoutIndex + impulseCheckBars);
    index += 1
  ) {
    bestClose = isDemand
      ? Math.max(bestClose, candles[index].close)
      : Math.min(bestClose, candles[index].close);
  }

  return isDemand ? bestClose - boundary : boundary - bestClose;
}

export function resolveZoneState(zone, candles, options = DEFAULT_ZONE_OPTIONS) {
  let tested = false;
  let mitigated = false;
  let failed = false;
  let engaged = false;
  let testIndex = null;
  let failedIndex = null;
  let testDepth = null;
  let reactionStrength = null;

  for (let index = zone.breakoutIndex + 1; index < candles.length; index += 1) {
    const candle = candles[index];
    const overlap = candle.low <= zone.high && candle.high >= zone.low;
    const closesBeyondZone = isFailureClose(zone, candle, options);
    const proximityRetest = isProximityRetest(zone, candle, options);
    const retestDepth = overlap ? getRetestDepth(zone, candle) : 0;
    const rejected = hasDirectionalRejection(zone, candle);
    const spikeRetest = overlap && isSpikeCandle(candle, candles, index, options);

    if (failed) {
      break;
    }

    if (!overlap && !proximityRetest && !closesBeyondZone) {
      continue;
    }

    const engagedThisCandle = overlap || proximityRetest;

    if (closesBeyondZone && engaged) {
      failed = true;
      mitigated = true;
      failedIndex = index;
      if (testIndex == null) {
        testIndex = index;
      }
      break;
    }

    if (!tested && overlap && (spikeRetest || rejected)) {
      tested = true;
      mitigated = true;
      testIndex = index;
      testDepth = retestDepth;
      reactionStrength = spikeRetest ? "spike" : "rejection";
      continue;
    }

    if (!tested && !overlap && proximityRetest && rejected) {
      tested = true;
      mitigated = true;
      testIndex = index;
      testDepth = 0;
      reactionStrength = "proximity";
    }

    if (engagedThisCandle) {
      engaged = true;
    }
  }

  const eventIndex = failedIndex ?? testIndex;
  const state = failed ? "failed" : tested ? "tested" : "fresh";

  return {
    ...zone,
    tested,
    mitigated,
    failed,
    state,
    valid: !failed,
    name: zone.zoneLabel,
    label: getZoneDisplayLabel(zone, { failed, tested }),
    testIndex,
    testDepth,
    reactionStrength,
    failedIndex,
    testTime: eventIndex == null ? null : candles[eventIndex]?.time ?? null,
    failedTime: failedIndex == null ? null : candles[failedIndex]?.time ?? null,
  };
}

export function buildZone(baseZone, candles, options, averageCandleRange) {
  const resolvedZone = resolveZoneState(baseZone, candles, options);

  return {
    ...resolvedZone,
    qualityScore: computeZoneQuality(resolvedZone, averageCandleRange),
  };
}

export function sameDirectionalOverlap(left, right) {
  if (left.isDemand !== right.isDemand) {
    return false;
  }

  const overlapHigh = Math.min(left.high, right.high);
  const overlapLow = Math.max(left.low, right.low);
  const overlapSize = Math.max(0, overlapHigh - overlapLow);
  const minRange = Math.max(0.0000001, Math.min(left.range, right.range));

  return overlapSize / minRange >= 0.72;
}

export function createDirectionalZoneDetector({
  zoneName,
  zoneLabel,
  isDemand,
}) {
  return function detectDirectionalZones(candles, rawOptions = {}) {
    const analyzedCandles = getAnalyzedCandles(candles);
    const options = {
      ...DEFAULT_ZONE_OPTIONS,
      ...rawOptions,
    };

    if (
      analyzedCandles.length <
      options.consolidationBars + options.impulseCheckBars + 2
    ) {
      return {
        zoneName,
        zones: [],
        analyzedCount: analyzedCandles.length,
        averageCandleRange: 0,
        options,
      };
    }

    const averageCandleRange = averageRange(analyzedCandles);
    const maxConsolidationSpread =
      averageCandleRange * options.maxConsolidationSpreadMultiplier;
    const minZoneSize = averageCandleRange * options.minZoneSizeMultiplier;
    const maxZoneSize = averageCandleRange * options.maxZoneSizeMultiplier;
    const breakoutBuffer = averageCandleRange * 0.08;
    const zones = [];

    for (
      let breakoutIndex = options.consolidationBars;
      breakoutIndex < analyzedCandles.length - options.impulseCheckBars;
      breakoutIndex += 1
    ) {
      const rangeStartIndex = breakoutIndex - options.consolidationBars;
      const rangeEndIndex = breakoutIndex - 1;
      const zoneRange = getRangeStats(
        analyzedCandles,
        rangeStartIndex,
        rangeEndIndex
      );

      if (
        zoneRange.range <= 0 ||
        zoneRange.range > maxConsolidationSpread ||
        zoneRange.range < minZoneSize ||
        zoneRange.range > maxZoneSize
      ) {
        continue;
      }

      const breakoutCandle = analyzedCandles[breakoutIndex];
      const matchesDirection = isDemand
        ? (breakoutCandle.close >= zoneRange.high ||
            breakoutCandle.high > zoneRange.high + breakoutBuffer) &&
          breakoutCandle.close >= zoneRange.low
        : (breakoutCandle.close <= zoneRange.low ||
            breakoutCandle.low < zoneRange.low - breakoutBuffer) &&
          breakoutCandle.close <= zoneRange.high;

      if (!matchesDirection) {
        continue;
      }

      const threshold = zoneRange.range * options.impulseMultiplier;
      let impulseConfirmed = false;

      for (
        let impulseIndex = breakoutIndex;
        impulseIndex <= breakoutIndex + options.impulseCheckBars;
        impulseIndex += 1
      ) {
        const impulseCandle = analyzedCandles[impulseIndex];

        if (!impulseCandle) {
          break;
        }

        if (
          (isDemand && impulseCandle.close >= zoneRange.high + threshold) ||
          (!isDemand && impulseCandle.close <= zoneRange.low - threshold)
        ) {
          impulseConfirmed = true;
          break;
        }
      }

      if (!impulseConfirmed) {
        continue;
      }

      const nextZone = buildZone(
        {
          id: `${zoneName}-${breakoutCandle.time}`,
          name: zoneLabel,
          zoneName,
          zoneLabel,
          high: zoneRange.high,
          low: zoneRange.low,
          range: zoneRange.range,
          startIndex: rangeStartIndex,
          endIndex: rangeEndIndex,
          breakoutIndex,
          startTime: analyzedCandles[rangeStartIndex].time,
          breakoutTime: breakoutCandle.time,
          isDemand,
          type: isDemand ? "demand" : "supply",
          impulseDistance: scoreImpulse(
            analyzedCandles,
            breakoutIndex,
            options.impulseCheckBars,
            isDemand ? zoneRange.high : zoneRange.low,
            isDemand
          ),
        },
        analyzedCandles,
        options,
        averageCandleRange
      );

      const existingIndex = zones.findIndex((zone) =>
        sameDirectionalOverlap(zone, nextZone)
      );

      if (existingIndex >= 0) {
        if (zones[existingIndex].impulseDistance >= nextZone.impulseDistance) {
          continue;
        }

        zones.splice(existingIndex, 1, nextZone);
        continue;
      }

      zones.push(nextZone);
    }

    return {
      zoneName,
      zones: zones.sort((left, right) => {
        if ((right.qualityScore ?? 0) !== (left.qualityScore ?? 0)) {
          return (right.qualityScore ?? 0) - (left.qualityScore ?? 0);
        }

        if ((right.impulseDistance ?? 0) !== (left.impulseDistance ?? 0)) {
          return (right.impulseDistance ?? 0) - (left.impulseDistance ?? 0);
        }

        return (right.breakoutTime ?? 0) - (left.breakoutTime ?? 0);
      }),
      analyzedCount: analyzedCandles.length,
      averageCandleRange,
      options,
    };
  };
}
